import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

// Definindo a estrutura de uma seção
export interface StatItem {
  id?: string;
  number: number;
  label: string;
  prefix?: string;
  suffix?: string;
  [key: string]: any;
}

export interface ServiceItem {
  id?: string;
  icon: string;
  title: string;
  description: string;
  [key: string]: any;
}

export interface SectionContent {
  title: string;
  subtitle: string;
  description: string;
  subDescription: string;
  cta: string;
  stats?: StatItem[];
  services?: ServiceItem[];
  [key: string]: any;
}

export interface Section {
  id: string
  name: string
  content: SectionContent
}

// Estado inicial das seções
const initialSections: Section[] = [
  {
    id: 'hero',
    name: 'Seção Hero',
    content: {
      title: 'Serralheria e Estruturas',
      subtitle: 'Metálicas de Precisão',
      description: 'Projetos sob medida para sua necessidade.',
      subDescription: 'Portões, móveis industriais, projetos especiais e manutenções.',
      cta: 'ORÇAMENTO SEM COMPROMISSO',
    },
  },
  {
    id: 'services',
    name: 'Seção Serviços',
    content: {
      title: 'Nossos Serviços',
      subtitle: 'Soluções em Serralheria',
      description: 'Oferecemos serviços completos de serralheria e estruturas metálicas.',
      subDescription: 'Qualidade, durabilidade e preço justo.',
      cta: 'CONHEÇA NOSSOS SERVIÇOS',
      services: [
        {
          icon: 'FaDoorClosed',
          title: 'Portões e Grades',
          description: 'Design e fabricação de portões, grades e cercas metálicas sob medida para residências e empresas.'
        },
        {
          icon: 'FaBuilding',
          title: 'Estruturas Metálicas',
          description: 'Projetamos e construímos mezaninos, coberturas, galpões e outras estruturas metálicas robustas.'
        },
        {
          icon: 'FaChair',
          title: 'Móveis Estilo Industrial',
          description: 'Criação de móveis únicos com design industrial, como mesas, estantes e bancadas.'
        },
        {
          icon: 'FaPalette',
          title: 'Serralheria Artística',
          description: 'Peças decorativas e funcionais em metal, com design exclusivo e acabamento impecável.'
        },
        {
          icon: 'FaTools',
          title: 'Manutenção e Reparos',
          description: 'Serviços de manutenção preventiva e corretiva em estruturas metálicas e portões.'
        },
        {
          icon: 'FaCogs',
          title: 'Projetos Especiais',
          description: 'Desenvolvemos soluções personalizadas em metal para atender às necessidades específicas do seu projeto.'
        }
      ]
    },
  },
  {
    id: 'stats',
    name: 'Seção Estatísticas',
    content: {
      title: 'Nossos Números',
      subtitle: 'Resultados que falam por si',
      description: 'Anos de experiência no mercado de estruturas metálicas.',
      subDescription: 'Centenas de clientes satisfeitos em todo o Brasil.',
      cta: 'VEJA NOSSOS PROJETOS',
      stats: [
        {
          number: 5,
          label: 'anos de experiência',
          prefix: '+',
        },
        {
          number: 100,
          label: 'projetos concluídos',
          prefix: '+',
        },
        {
          number: 50,
          label: 'clientes satisfeitos',
          prefix: '+',
        },
        {
          number: 99,
          label: 'de aprovação',
          suffix: '%',
        },
      ]
    },
  },
]

// Interface do contexto
interface ContentContextType {
  sections: Section[]
  setSections: React.Dispatch<React.SetStateAction<Section[]>>
  updateSection: (sectionId: string, field: string, value: any) => void
  getSection: (sectionId: string) => Section | undefined
  saveChanges: () => Promise<boolean>
  isSaving: boolean
  saveMessage: string
}

// Criando o contexto
const ContentContext = createContext<ContentContextType | undefined>(undefined)

// Hook para usar o contexto
export const useContent = () => {
  const context = useContext(ContentContext)
  if (!context) {
    throw new Error('useContent deve ser usado dentro de um ContentProvider')
  }
  return context
}

// Provider do contexto
interface ContentProviderProps {
  children: ReactNode
}

// Chave para armazenar no localStorage
const STORAGE_KEY = 'uniao_ferr_content';

export const ContentProvider = ({ children }: ContentProviderProps) => {
  // Carrega os dados salvos do localStorage ou usa os valores iniciais
  const loadSections = (): Section[] => {
    if (typeof window === 'undefined') return initialSections;
    
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : initialSections;
    } catch (error) {
      console.error('Erro ao carregar conteúdo do localStorage:', error);
      return initialSections;
    }
  };

  const [sections, setSections] = useState<Section[]>(loadSections);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [channel, setChannel] = useState<BroadcastChannel | null>(null);

  // Inicializa o BroadcastChannel para sincronização entre abas
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const broadcastChannel = new BroadcastChannel('content_updates');
      setChannel(broadcastChannel);

      // Escuta por atualizações de outras abas
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'CONTENT_UPDATED') {
          console.log('Recebida atualização de conteúdo de outra aba');
          setSections(event.data.sections);
        }
      };

      broadcastChannel.addEventListener('message', handleMessage);

      return () => {
        broadcastChannel.removeEventListener('message', handleMessage);
        broadcastChannel.close();
      };
    }
  }, []);

  // Função para atualizar uma seção
  const updateSection = (sectionId: string, field: string, value: any) => {
    console.log('ContentContext - Updating section:', sectionId, field, value);
    
    // Cria uma cópia profunda das seções para garantir que a referência seja alterada
    const updatedSections = JSON.parse(JSON.stringify(sections));
    
    // Encontra e atualiza a seção específica
    const sectionIndex = updatedSections.findIndex((section: Section) => section.id === sectionId);
    
    if (sectionIndex !== -1) {
      // Se o campo contiver pontos, significa que é um caminho aninhado (ex: 'services.0.title')
      if (field.includes('.')) {
        const [parentField, index, childField] = field.split('.');
        const arrayIndex = parseInt(index, 10);
        
        if (!isNaN(arrayIndex) && Array.isArray(updatedSections[sectionIndex].content[parentField])) {
          // Atualiza o campo aninhado no array
          updatedSections[sectionIndex].content[parentField][arrayIndex][childField] = value;
        }
      } else {
        // Atualização normal de campo
        updatedSections[sectionIndex].content[field] = value;
      }
      
      setSections(updatedSections);
      console.log('ContentContext - Sections after update:', updatedSections);
    } else {
      console.error(`Seção com ID ${sectionId} não encontrada`);
    }
  }

  // Função para obter uma seção pelo ID
  const getSection = (sectionId: string) => {
    return sections.find((section) => section.id === sectionId)
  }

  // Função para salvar as alterações no localStorage e sincronizar entre abas
  const saveChanges = async (): Promise<boolean> => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      // Salva no localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
      
      // Envia atualização para outras abas
      if (channel) {
        channel.postMessage({
          type: 'CONTENT_UPDATED',
          sections
        });
      }
      
      setSaveMessage('Conteúdo atualizado com sucesso!');
      
      // Limpa a mensagem após 3 segundos
      setTimeout(() => {
        setSaveMessage('');
      }, 3000);
      
      return true;
    } catch (error) {
      console.error('Erro ao salvar conteúdo:', error);
      setSaveMessage('Erro ao salvar as alterações. Tente novamente.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  const value = {
    sections,
    setSections,
    updateSection,
    getSection,
    saveChanges,
    isSaving,
    saveMessage,
  }

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>
}
